import { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  InputAdornment,
  IconButton,
  Dialog,
  DialogContent,
  CircularProgress,
} from "@mui/material";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import logo from "../../assets/images/main_logo.png";
import "../../styles/header.css";
import BASE_URL from "../../components/config/appconfig";
import SnackbarAlert from "../../components/snackbarAlert/snackbarAlert";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined";
import usePageMeta from "../../utils/usePageMeta";

const CTCAP_EMAIL = "churchhealth@citytocityasiapacific.com";
export default function SignIn() {
  usePageMeta(
    "Sign In",
    "Sign in to access the Gospel and Church Health Framework."
  );
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({
    email: "",
    password: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resendOpen, setResendOpen] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [resendEmailError, setResendEmailError] = useState("");
  const [resendLoading, setResendLoading] = useState(false);

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const validateField = (field, value) => {
    let error = "";
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      error = "This field is required";
    } else if (field === "email" && !validateEmail(trimmedValue)) {
      error = "Enter a valid email address";
    } else if (field === "password" && trimmedValue.length < 6) {
      error = "Password must be at least 6 characters";
    }

    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));

    return !error;
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    validateField(field, value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const emailValid = validateField("email", formData.email);
    const passwordValid = validateField("password", formData.password);

    if (!emailValid || !passwordValid) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/admin/user-role`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
        }),
      });

      let result;
      try {
        result = await response.json();
      } catch {
        setSnackbar({
          open: true,
          message: "Invalid email or password",
          severity: "error",
        });
        return;
      }

      if (result.success) {
        localStorage.setItem("userData", JSON.stringify(result.user));
        if (result.token) {
          localStorage.setItem("authToken", result.token);
        }

        setTimeout(() => {
          switch (result.nextStep) {
            case "admin-dashboard":
              navigate("/admin-dashboard", { replace: true });
              break;

            case "assessment":
              navigate("/assessment", {
                state: { assessmentId: result.assessmentId },
                replace: true,
              });
              break;

            case "feedback":
              navigate(`/feedback/${result.assessmentId}`, { replace: true });
              break;

            case "payment":
              navigate("/payment", { replace: true });
              break;

            default:
              navigate("/verification", { replace: true });
          }
        }, 1200);
      } else {
        setIsLoading(false);
        setSnackbar({
          open: true,
          message: result.message || "Invalid credentials",
          severity: "error",
        });
      }
    } catch (err) {
      setIsLoading(false);
      setSnackbar({
        open: true,
        message: "Something went wrong. Please try again.",
        severity: "error",
      });
    }
  };

  const handleResendConfirmation = async () => {
    if (!validateEmail(resendEmail.trim())) {
      setResendEmailError("Enter a valid email address");
      return;
    }
    setResendEmailError("");
    setResendLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/api/admin/resend-confirmation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: resendEmail.trim() }),
      });
      const result = await res.json();
      setResendOpen(false);
      setResendEmail("");
      setSnackbar({
        open: true,
        message:
          result.message ||
          (result.success
            ? "Confirmation email sent!"
            : "Failed to resend email"),
        severity: result.success ? "success" : "error",
      });
    } catch {
      setResendOpen(false);
      setSnackbar({
        open: true,
        message: "Something went wrong. Please try again.",
        severity: "error",
      });
    } finally {
      setResendLoading(false);
    }
  };

  const hideSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
        width: "100%",
      }}
    >
      {/* Logo header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: { xs: 2, sm: 4 },
          py: 2,
          bgcolor: "#FFFFFF",
        }}
      >
        <img
          src={logo}
          alt="City to City Asia Pacific"
          style={{ height: "auto", width: "clamp(120px, 20vw, 160px)" }}
        />
        <Button
          variant="outlined"
          startIcon={<HeadsetMicOutlinedIcon sx={{ fontSize: 14 }} />}
          onClick={() => window.open(`mailto:${CTCAP_EMAIL}`, "_blank")}
          sx={{
            bgcolor: "transparent",
            border: "1px solid #9e9e9e",
            color: "#757575",
            "&:hover": { bgcolor: "transparent", borderColor: "#757575" },
            fontSize: 11,
            fontWeight: 700,
            borderRadius: 2,
            px: 2,
            py: 1,
            boxShadow: "none",
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          Contact Us
        </Button>
      </Box>

      {/* Main content container */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          px: { xs: 2, sm: 4, md: 6 },
          py: { xs: 2, sm: 4, md: 6 },
          width: "100%",
          maxWidth: "1600px",
          mx: "auto",
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            justifyContent: "space-between",
            gap: { xs: 2, sm: 4, md: 6 },
            width: "100%",
          }}
        >
          {/* LEFT SIDE (TEXT) */}
          <Box sx={{ flex: 1, width: "100%", pl: { md: 4 } }}>
            <Typography
              sx={{
                fontFamily: "'Gotham Black', sans-serif",
                fontWeight: 900,
                fontSize: { xs: "28px", sm: "48px", md: "64px" },
                lineHeight: 1.1,
                color: "#0d1c26",
                mb: 2,
                letterSpacing: "-1px",
                textAlign: { xs: "center", md: "left" },
              }}
            >
              The Gospel and Church Health Framework
            </Typography>
            <Typography
              sx={{
                color: "#009ccc",
                fontSize: { xs: "11px", sm: "14px" },
                fontWeight: 700,
                letterSpacing: { xs: "1px", sm: "3px" },
                mb: { xs: 1, sm: 6 },
                textAlign: { xs: "center", md: "left" },
              }}
            >
              A reflection and discussion tool for pastors and leaders
            </Typography>
          </Box>

          {/* RIGHT SIDE (SIGN IN FORM) */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{
                width: "100%",
                maxWidth: "420px",
                bgcolor: "#fff",
                borderRadius: 5,
                border: "1px solid #e8e8e8",
                p: { xs: 3, sm: 5 },
                "& .MuiInputBase-input": { fontSize: "14px" },
              }}
            >
              <Typography
                variant="h5"
                mb={2}
                sx={{ fontWeight: 600, fontSize: { xs: "20px", sm: "22px" } }}
              >
                Sign In
              </Typography>

              {/* EMAIL */}
              <Typography className="editor-dialog-label" mt={1}>
                Email Address <span className="required-star">*</span>
              </Typography>
              <TextField
                fullWidth
                required
                placeholder="Enter Email Address"
                value={formData.email}
                onChange={handleChange("email")}
                error={!!errors.email}
                helperText={errors.email}
                size="small"
              />

              {/* PASSWORD */}
              <Typography className="editor-dialog-label" mt={2}>
                Password <span className="required-star">*</span>
              </Typography>
              <TextField
                fullWidth
                required
                placeholder="Enter Password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleChange("password")}
                error={!!errors.password}
                helperText={errors.password}
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleTogglePassword} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* BUTTON */}
              <Box sx={{ mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isLoading}
                  className="next-button"
                  sx={{
                    width: "100%",
                    height: 48,
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": { boxShadow: "none" },
                    opacity: isLoading ? 0.8 : 1,
                  }}
                >
                  {isLoading ? (
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        border: "2px solid rgba(255,255,255,0.4)",
                        borderTopColor: "#fff",
                        borderRadius: "50%",
                        animation: "spin 0.7s linear infinite",
                        "@keyframes spin": {
                          from: { transform: "rotate(0deg)" },
                          to: { transform: "rotate(360deg)" },
                        },
                      }}
                    />
                  ) : (
                    "Sign In"
                  )}
                </Button>
              </Box>

              {/* LINKS */}
              <Box sx={{ textAlign: "center", mt: 2 }}>
                <Typography fontSize={{ xs: 13, sm: 14 }}>
                  <span style={{ color: "#263842" }}>
                    Don't have an account?{" "}
                  </span>
                  <Link
                    component={RouterLink}
                    to="/"
                    underline="hover"
                    sx={{ color: "#263842", fontWeight: 600 }}
                  >
                    Sign Up
                  </Link>
                </Typography>

                <Typography fontSize={{ xs: 13, sm: 14 }} mt={1}>
                  <Link
                    component={RouterLink}
                    to="/forgot-password"
                    underline="hover"
                    sx={{ color: "#263842", fontWeight: 600 }}
                  >
                    Forgot Password?
                  </Link>
                </Typography>

                <Typography fontSize={{ xs: 13, sm: 14 }} mt={1}>
                  <span style={{ color: "#888" }}>
                    Didn't receive the confirmation email?{" "}
                  </span>
                  <span
                    onClick={() => {
                      setResendEmail("");
                      setResendEmailError("");
                      setResendOpen(true);
                    }}
                    style={{
                      color: "#263842",
                      fontWeight: 600,
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                  >
                    Resend it
                  </span>
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <SnackbarAlert
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={hideSnackbar}
      />

      <Dialog
        open={resendOpen}
        onClose={() => setResendOpen(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: "12px" } } }}
      >
        <DialogContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Typography fontWeight={700} fontSize="18px" mb={0.5}>
            Resend Confirmation Email
          </Typography>
          <Typography fontSize="13px" color="#888" mb={3}>
            Enter your email address and we'll resend the confirmation link.
          </Typography>

          <Typography className="editor-dialog-label" mb={0.5}>
            Email Address <span className="required-star">*</span>
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter your email"
            value={resendEmail}
            onChange={(e) => {
              setResendEmail(e.target.value);
              setResendEmailError("");
            }}
            error={!!resendEmailError}
            helperText={resendEmailError}
            sx={{ "& .MuiInputBase-input": { fontSize: "14px" } }}
          />

          <Box display="flex" justifyContent="flex-end" gap={1.5} mt={3}>
            <Button
              variant="outlined"
              onClick={() => setResendOpen(false)}
              sx={{
                textTransform: "none",
                fontSize: 13,
                borderColor: "#d0d0d0",
                color: "#555",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={resendLoading}
              onClick={handleResendConfirmation}
              sx={{
                textTransform: "none",
                fontSize: 13,
                bgcolor: "#ed1c2e",
                boxShadow: "none",
                "&:hover": { bgcolor: "#d41727", boxShadow: "none" },
                minWidth: 120,
              }}
            >
              {resendLoading ? (
                <CircularProgress size={16} sx={{ color: "#fff" }} />
              ) : (
                "Resend Email"
              )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
